import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Grid,
  Image,
  Badge,
  Stack,
  Divider,
  Rating,
  Paper,
  Box,
  NumberInput,
  Tabs,
  List,
  ThemeIcon,
  SimpleGrid,
  ActionIcon,
  Modal,
  TextInput,
  Select,
  useMantineTheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState, useEffect, useRef } from 'react';
import * as modelViewer from '@google/model-viewer';
import {
  IconShoppingCart,
  IconHeart,
  IconShare,
  IconCheck,
  IconTrash,
  IconTruck,
  IconShield,
  IconStarFilled,
  IconSparkles,
  IconDroplet,
  IconBolt,
  IconWand,
  IconFlame,
  IconRefresh,
  IconPackage,
  IconChevronLeft,
  IconChevronRight,
  IconX,
  IconCreditCard,
  IconMail,
  IconPhone,
  IconMapPin,
} from '@tabler/icons-react';
import { pageWrapper } from '@/Styles';
import { websiteDomain } from '@/ApiPaths';
import { MODEL_VIEWER_PRESENTATION_ATTRS } from '@/constants/modelViewerPresentation';
import { MODEL_VIEWER_RELAXED_ZOOM_ATTRS } from '@/utils/annotationConstants';
import { useModelViewerDynamicZoom } from '@/hooks/useModelViewerDynamicZoom';
import { ModelViewerResetZoomButton } from '@/components/ModelViewer/ModelViewerResetZoomButton';
import { ModelViewerScreenshotButton } from '@/components/ModelViewer/ModelViewerScreenshotButton';
// import * as modelViewer from '@google/model-viewer'; // This import is unnecessary if you are using the <model-viewer> web component directly in JSX, so it can be removed.
// Remove glb import; instead use the public path string during usage.
const FIREPLACE_MODEL_SRC = '/dropshipping.glb';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  color: string | null;
  image: string;
}
type Hotspot = {
  id: string;
  label: string;
  position: string;
  normal?: string;
  orbit?: string;
  target?: string;
  title: string;
  text: string;
};

const demoHotspots: Hotspot[] = [
  {
    id: '1',
    label: '1',
    position: '0m 0.12m 0.03m',
    normal: '0 1 0',
    orbit: '15deg 65deg 0.55m',
    target: '0m 0.12m 0.03m',
    title: 'Flame Effect Area',
    text: 'Shows the realistic fireplace-style flame effect and mist outlet.',
  },
  {
    id: '2',
    label: '2',
    position: '0m 0.05m 0.09m',
    normal: '0 1 0',
    orbit: '-25deg 70deg 0.6m',
    target: '0m 0.05m 0.09m',
    title: 'Water Tank',
    text: '150 ml tank – fill with water and a few drops of essential oil.',
  },
  {
    id: '3',
    label: '3',
    position: '-0.09m 0.03m 0m',
    normal: '0 1 0',
    orbit: '115deg 70deg 0.65m',
    target: '-0.09m 0.03m 0m',
    title: 'Controls & Ports',
    text: 'Power input and control buttons for lights, mist and timer.',
  },
];



export function DropshippingDemo() {
  const theme = useMantineTheme();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [isStickyVisible, setIsStickyVisible] = useState(false);
  const [show3DViewer, setShow3DViewer] = useState(false);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpened, setCartOpened] = useState(false);
  const [checkoutOpened, setCheckoutOpened] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    paymentMethod: '',
  });
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const modelViewerRef = useRef<modelViewer.ModelViewerElement | null>(null);
  useModelViewerDynamicZoom(modelViewerRef, show3DViewer ? FIREPLACE_MODEL_SRC : null);
  const [hotspots] = useState<Hotspot[]>(demoHotspots);
  const [activeHotspot, setActiveHotspot] = useState<Hotspot | null>(null);

  // Check if product is saved on mount
  useEffect(() => {
    const savedProducts = JSON.parse(localStorage.getItem('savedProducts') || '[]');
    const productId = 'fireplace-aroma-diffuser'; // Unique product ID
    setIsSaved(savedProducts.includes(productId));
  }, []);

  // Generate image paths
  const productImages = Array.from({ length: 10 }, (_, i) => `/ds-photos/main-image-${i + 2}.jpeg`);

  // Product data - KINSCOTER Fireplace Aroma Diffuser
  const product = {
    name: 'Fireplace Aroma Diffuser',
    subtitle: 'Elevate Your Space with Ambiance & Wellness',
    shortName: 'KINSCOTER',
    price: 39.99,
    originalPrice: 59.99,
    rating: 4.8,
    reviews: 1247,
    images: productImages,
    colors: ['Black', 'White', 'Brown', 'Gray'],
    description: `Transform your living space with the KINSCOTER Fireplace Aroma Diffuser, a beautiful combination of aromatherapy and ambient lighting. This innovative device features a realistic flame effect that creates a cozy fireplace atmosphere without the fire hazards. Perfect for relaxation, meditation, or simply enhancing your home decor.

The diffuser uses ultrasonic technology to create a fine mist that evenly disperses your favorite essential oils throughout the room. With a generous 150ml water capacity and a mist output of 20-25ml/h, it provides continuous humidification and aromatherapy for hours. The colorful LED ambient lighting adds to the relaxing atmosphere, while the included remote control allows you to adjust settings from the comfort of your couch.

Ideal for bedrooms, living rooms, offices, or any space where you want to create a peaceful, aromatic environment. Makes a thoughtful gift for anyone who loves home decor, aromatherapy, or simply enjoys creating a cozy atmosphere.`,
    features: [
      'Water Shortage Protection & Timer: Intelligent functions for safe and efficient operation',
      'Ultrasonic Humidification: Uses ultrasonic technology for even and quiet humidification without water damage concerns',
      'USB Power Supply: Easy power supply via USB, perfect for use in any living room',
      'LED Light & Remote Control: Atmospheric LED lighting with convenient remote control for easy operation',
      'Realistic Fireplace Effect: Beautiful simulated flame effect creates cozy living room ambiance',
      '150ml Capacity, 20-25 ml/h Mist Output: Continuous humidity with generous capacity',
      'Aromatherapy Function: Perfect for dispersing essential oils and creating a relaxing atmosphere',
      'Colorful Ambient Nightlight: Multi-color LED lighting options for mood setting',
      'CE & RoHS Certified: Safe and environmentally friendly materials',
      'BPA-Free ABS Plastic: Made from eco-friendly materials',
    ],
    specifications: {
      model: 'DQ717',
      capacity: '150 ml',
      mistOutput: '20-25 ml/h',
      powerSupply: 'USB (5V 2A adapter recommended)',
      control: 'Remote Control',
      function: 'Aromatherapy, Humidification, Ambient Lighting',
      size: '21.8 x 8.5 x 10.7 cm (8.58 x 3.35 x 4.21 inches)',
      material: 'ABS Plastic (BPA-free), Eco-friendly PP, Electronic Components',
      certification: 'CE, RoHS',
    },
    packageContents: [
      'Main unit of aroma diffuser x1',
      'Remote control x1',
      'Type-C power cable x1',
      'User manual x1',
    ],
    purchaseNotes: [
      'Please check after receiving the product to ensure it is intact and undamaged. If you encounter any issues, please contact our customer service team.',
      'This product requires a 5V 2A adapter power supply to achieve the optimal flame effect, as shown in the image.',
      'Please note that when adding water, ensure it does not enter the ventilation channel or touch the USB port to avoid product damage.',
    ],
    shipping: 'Free shipping on orders over $40',
    returns: '30-day money-back guarantee',
    stock: 243,
  };

  // Handle scroll for sticky cart
  useEffect(() => {
    const handleScroll = () => {
      setIsStickyVisible(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % product.images.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + product.images.length) % product.images.length);
  };
  const handleHotspotClick = (hotspot: Hotspot) => {
    const mv = modelViewerRef.current;
    if (!mv) return;

    if (hotspot.target) mv.cameraTarget = hotspot.target;
    if (hotspot.orbit) mv.cameraOrbit = hotspot.orbit;
    mv.fieldOfView = '30deg'; // biraz zoom

    setActiveHotspot(hotspot);
  };

  // Cart functions
  const addToCart = () => {
    const newItem: CartItem = {
      id: `${product.shortName}-${selectedColor || 'default'}-${Date.now()}`,
      name: product.name,
      price: product.price,
      quantity: quantity,
      color: selectedColor,
      image: product.images[0],
    };

    setCart((prev) => {
      // Check if same item (same color) exists, update quantity
      const existingItemIndex = prev.findIndex(
        (item) => item.name === newItem.name && item.color === newItem.color
      );

      if (existingItemIndex >= 0) {
        const updated = [...prev];
        updated[existingItemIndex].quantity += newItem.quantity;
        return updated;
      }

      return [...prev, newItem];
    });

    // Reset quantity
    setQuantity(1);
    // Open cart modal
    setCartOpened(true);
  };

  const removeFromCart = (id: string) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  };

  const updateCartQuantity = (id: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(id);
      return;
    }
    setCart((prev) =>
      prev.map((item) => (item.id === id ? { ...item, quantity: newQuantity } : item))
    );
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartItemsCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const handleSave = () => {
    const productId = 'fireplace-aroma-diffuser';
    const savedProducts = JSON.parse(localStorage.getItem('savedProducts') || '[]');

    if (isSaved) {
      // Remove from saved
      const updated = savedProducts.filter((id: string) => id !== productId);
      localStorage.setItem('savedProducts', JSON.stringify(updated));
      setIsSaved(false);
      notifications.show({
        title: 'Removed from favorites',
        message: `${product.name} has been removed from your favorites.`,
        color: 'gray',
        icon: <IconHeart size={18} />,
      });
    } else {
      // Add to saved
      savedProducts.push(productId);
      localStorage.setItem('savedProducts', JSON.stringify(savedProducts));
      setIsSaved(true);
      notifications.show({
        title: 'Saved to favorites',
        message: `${product.name} has been added to your favorites.`,
        color: 'pink',
        icon: <IconCheck size={18} />,
      });
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      notifications.show({
        title: 'Link copied',
        message: 'Product link has been copied to clipboard!',
        color: 'blue',
        icon: <IconCheck size={18} />,
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to copy link. Please try again.',
        color: 'red',
      });
    }
  };

  const handleCheckout = () => {
    setCartOpened(false);
    setCheckoutOpened(true);
  };

  const handlePlaceOrder = () => {
    // Simulate order placement
    setOrderPlaced(true);
    setCart([]);
    setCheckoutForm({
      fullName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postalCode: '',
      country: '',
      paymentMethod: '',
    });

    // Close checkout modal after 2 seconds
    setTimeout(() => {
      setCheckoutOpened(false);
      setOrderPlaced(false);
    }, 2000);
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)',
      }}
    >
      {/* Hero Section - Light Theme */}
      <Box
        style={{
          position: 'relative',
          paddingTop: '20px',
          paddingBottom: '40px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #f0f4f8 100%)',
          overflow: 'hidden',
        }}
      >
        {/* Decorative gradient orbs */}
        <Box
          style={{
            position: 'absolute',
            top: '-200px',
            right: '-200px',
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background:
              'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
            filter: 'blur(80px)',
          }}
        />
        <Box
          style={{
            position: 'absolute',
            bottom: '-300px',
            left: '-300px',
            width: '800px',
            height: '800px',
            borderRadius: '50%',
            background:
              'linear-gradient(135deg, rgba(59, 130, 246, 0.06) 0%, rgba(147, 51, 234, 0.06) 100%)',
            filter: 'blur(100px)',
          }}
        />

        <Container size="xl">
          <Grid align="center" gutter={{ base: 'xl', md: 80 }}>
            {/* Left Side - Hero Image Gallery */}
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Box
                style={{
                  position: 'relative',
                  borderRadius: '32px',
                  overflow: 'hidden',
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 25px 80px rgba(0, 0, 0, 0.08)',
                }}
              >
                <Box
                  style={{
                    position: 'relative',
                    aspectRatio: '4/3',
                    overflow: 'hidden',
                  }}
                >
                  {show3DViewer ? (
                    <Box
                      style={{
                        width: '100%',
                        height: '100%',
                        background: '#000',
                        position: 'relative',
                      }}
                    >
                      <ModelViewerResetZoomButton
                        modelViewerRef={modelViewerRef}
                        cameraDefaults={{
                          cameraOrbit: '35deg 70deg 0.75m',
                          cameraTarget: '0m 0.1m 0m',
                        }}
                      />
                      <ModelViewerScreenshotButton
                        modelViewerRef={modelViewerRef}
                        fileName="dropshipping-demo"
                      />
                      <model-viewer
                        ref={modelViewerRef}
                        style={{
                          width: '100%',
                          height: '100%',
                          display: 'block',
                          background: '#000',
                        }}
                        src={FIREPLACE_MODEL_SRC}
                        camera-controls
                        {...MODEL_VIEWER_RELAXED_ZOOM_ATTRS}
                        touch-action="pan-y"
                        {...MODEL_VIEWER_PRESENTATION_ATTRS}
                        ar
                        ar-modes="webxr scene-viewer quick-look"
                        ar-scale="auto"
                        interaction-prompt="auto"
                        interaction-prompt-style="basic"
                        autoplay
                        camera-orbit="35deg 70deg 0.75m"
                        camera-target="0m 0.1m 0m"
                      >
                        {hotspots.map((hs) => (
                          <button
                            key={hs.id}
                            slot={`hotspot-${hs.id}`}
                            onClick={() => handleHotspotClick(hs)}
                            style={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '50%',
                              border: '2px solid #ffffff',
                              background: 'rgba(15, 23, 42, 0.9)',
                              color: '#ffffff',
                              fontSize: '13px',
                              fontWeight: 700,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transform: 'translate3d(-50%, -50%, 0)',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                            }}
                            data-position={hs.position}
                            {...(hs.normal ? { 'data-normal': hs.normal } : {})}
                          >
                            {hs.label}
                          </button>
                        ))}
                      </model-viewer>

                      {activeHotspot && (
                        <Box
                          style={{
                            position: 'absolute',
                            left: '16px',
                            bottom: '16px',
                            maxWidth: '260px',
                            padding: '10px 14px',
                            background: 'rgba(15,23,42,0.92)',
                            color: '#fff',
                            borderRadius: '10px',
                            fontSize: '13px',
                            lineHeight: 1.4,
                            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                            zIndex: 10,
                          }}
                        >
                          <Text fw={700} size="sm" mb={4}>
                            {activeHotspot.title}
                          </Text>
                          <Text
                            size="xs"
                            c="gray.0"
                            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                          >
                            {activeHotspot.text}
                          </Text>
                        </Box>
                      )}

                      <Badge
                        size="lg"
                        variant="light"
                        color="blue"
                        leftSection={<IconSparkles size={16} />}
                        style={{
                          position: 'absolute',
                          top: '24px',
                          left: '24px',
                          background: 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: 'blur(10px)',
                          fontWeight: 700,
                          fontSize: '13px',
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                        }}
                      >
                        3D Interactive Viewer
                      </Badge>
                    </Box>
                  ) : (
                    <Image
                      src={product.images[selectedImage]}
                      alt={product.name}
                      fit="cover"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'opacity 0.3s ease',
                      }}
                    />
                  )}

                  {/* Navigation arrows - Only show when viewing images */}
                  {!show3DViewer && (
                    <>
                      <ActionIcon
                        variant="filled"
                        size="xl"
                        radius="xl"
                        onClick={prevImage}
                        style={{
                          position: 'absolute',
                          left: '20px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'rgba(255, 255, 255, 0.95)',
                          color: '#1e293b',
                          backdropFilter: 'blur(10px)',
                          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                          zIndex: 10,
                        }}
                      >
                        <IconChevronLeft size={24} />
                      </ActionIcon>
                      <ActionIcon
                        variant="filled"
                        size="xl"
                        radius="xl"
                        onClick={nextImage}
                        style={{
                          position: 'absolute',
                          right: '20px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'rgba(255, 255, 255, 0.95)',
                          color: '#1e293b',
                          backdropFilter: 'blur(10px)',
                          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                          zIndex: 10,
                        }}
                      >
                        <IconChevronRight size={24} />
                      </ActionIcon>
                    </>
                  )}

                  {/* Image counter */}
                  {!show3DViewer && (
                    <Badge
                      size="lg"
                      variant="light"
                      style={{
                        position: 'absolute',
                        bottom: '24px',
                        right: '24px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        fontWeight: 600,
                      }}
                    >
                      {selectedImage + 1} / {product.images.length}
                    </Badge>
                  )}
                </Box>

                {/* Thumbnail Gallery */}
                <Box
                  p="lg"
                  style={{
                    background: '#f8f9fa',
                    borderTop: '1px solid #e2e8f0',
                  }}
                >
                  <Group gap="sm" justify="center">
                    {/* 3D Viewer Thumbnail */}
                    <Box
                      onClick={() => {
                        setShow3DViewer(true);
                        setSelectedImage(-1);
                        setActiveHotspot(null);
                      }}
                      style={{
                        width: 70,
                        height: 70,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: show3DViewer ? '3px solid #667eea' : '2px solid transparent',
                        transition: 'all 0.2s ease',
                        opacity: show3DViewer ? 1 : 0.6,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.transform = 'scale(1.08)';
                      }}
                      onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                        if (!show3DViewer) {
                          e.currentTarget.style.opacity = '0.6';
                        }
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <IconSparkles size={24} color="white" />
                      <Text size="xs" fw={700} c="white" style={{ fontSize: '10px' }}>
                        3D
                      </Text>
                    </Box>

                    {/* Image Thumbnails */}
                    {product.images.slice(0, 7).map((img, index) => (
                      <Box
                        key={index}
                        onClick={() => {
                          setSelectedImage(index);
                          setShow3DViewer(false);
                        }}
                        style={{
                          width: 70,
                          height: 70,
                          borderRadius: '12px',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          border:
                            selectedImage === index && !show3DViewer
                              ? '3px solid #667eea'
                              : '2px solid transparent',
                          transition: 'all 0.2s ease',
                          opacity: selectedImage === index && !show3DViewer ? 1 : 0.6,
                          background: 'white',
                        }}
                        onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                          e.currentTarget.style.opacity = '1';
                          e.currentTarget.style.transform = 'scale(1.08)';
                        }}
                        onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                          if (selectedImage !== index || show3DViewer) {
                            e.currentTarget.style.opacity = '0.6';
                          }
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        <Image
                          src={img}
                          alt={`${product.name} ${index + 1}`}
                          fit="cover"
                          style={{ width: '100%', height: '100%' }}
                        />
                      </Box>
                    ))}
                  </Group>
                </Box>
              </Box>
            </Grid.Col>

            {/* Right Side - Hero Content */}
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Stack gap="md" style={{ gap: 'var(--mantine-spacing-md)' }}>
                <Badge
                  size="lg"
                  variant="light"
                  color="blue"
                  leftSection={<IconSparkles size={14} />}
                  style={{
                    width: 'fit-content',
                    textTransform: 'uppercase',
                    letterSpacing: '2px',
                    fontSize: '11px',
                    fontWeight: 700,
                    background:
                      'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                  }}
                >
                  Premium Collection
                </Badge>

                <div>
                  <Text
                    size="sm"
                    fw={700}
                    c="blue"
                    style={{
                      textTransform: 'uppercase',
                      letterSpacing: '3px',
                      marginBottom: '16px',
                      color: '#667eea',
                    }}
                  >
                    {product.shortName}
                  </Text>
                  <h1
                    style={{
                      marginBottom: '12px',
                      lineHeight: '1.1',
                      letterSpacing: '-1px',
                      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      fontSize: '1.75rem',
                      fontWeight: 800,
                      margin: 0,
                    }}
                  >
                    {product.name}
                  </h1>
                  <Text
                    c="dimmed"
                    style={{
                      marginBottom: '20px',
                      fontSize: '1rem',
                      color: '#64748b',
                      lineHeight: 1.6,
                    }}
                  >
                    {product.subtitle}
                  </Text>
                </div>

                <Group gap="xs" align="center">
                  <Group gap="xs">
                    <Rating value={product.rating} fractions={2} readOnly size="sm" />
                    <Text size="sm" fw={600} c="dimmed" style={{ fontSize: '0.875rem' }}>
                      {product.rating}
                    </Text>
                    <Text size="xs" c="dimmed" style={{ fontSize: '0.75rem' }}>
                      ({product.reviews.toLocaleString()})
                    </Text>
                  </Group>
                </Group>

                <Group
                  gap="md"
                  mt="md"
                  align="flex-end"
                  style={{
                    gap: 'var(--mantine-spacing-md)',
                    marginTop: 'var(--mantine-spacing-md)',
                  }}
                >
                  <div>
                    <Text
                      size="xs"
                      c="dimmed"
                      fw={600}
                      style={{
                        marginBottom: '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                      }}
                    >
                      Starting at
                    </Text>
                    <Group gap="sm" align="baseline">
                      <Text
                        fw={900}
                        style={{
                          lineHeight: 1,
                          letterSpacing: '-2px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontSize: '2rem',
                        }}
                      >
                        ${product.price.toFixed(2)}
                      </Text>
                      <Text
                        td="line-through"
                        c="dimmed"
                        fw={500}
                        style={{ color: '#94a3b8', marginBottom: '8px', fontSize: '1rem' }}
                      >
                        ${product.originalPrice.toFixed(2)}
                      </Text>
                    </Group>
                    <Badge
                      color="red"
                      variant="light"
                      size="sm"
                      style={{
                        marginTop: '6px',
                        fontSize: '11px',
                        fontWeight: 700,
                      }}
                    >
                      Save{' '}
                      {Math.round(
                        ((product.originalPrice - product.price) / product.originalPrice) * 100
                      )}
                      %
                    </Badge>
                  </div>
                </Group>

                <Group gap="md" mt="md">
                  <Button
                    size="md"
                    leftSection={<IconShoppingCart size={20} />}
                    onClick={addToCart}
                    style={{
                      flex: 1,
                      minWidth: '180px',
                      height: '48px',
                      fontSize: '15px',
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4)';
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                    }}
                  >
                    Add to Cart {getCartItemsCount() > 0 && `(${getCartItemsCount()})`}
                  </Button>
                  <Button
                    size="md"
                    variant={isSaved ? 'filled' : 'outline'}
                    color={isSaved ? 'pink' : undefined}
                    leftSection={isSaved ? <IconCheck size={18} /> : <IconHeart size={18} />}
                    onClick={handleSave}
                    style={{
                      height: '48px',
                      borderWidth: '2px',
                      borderColor: isSaved ? '#ec4899' : '#e2e8f0',
                      fontSize: '15px',
                      fontWeight: 600,
                      background: isSaved
                        ? 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)'
                        : undefined,
                    }}
                  >
                    {isSaved ? 'Saved' : 'Save'}
                  </Button>
                  <Button
                    size="md"
                    variant="subtle"
                    leftSection={<IconShare size={18} />}
                    onClick={handleShare}
                    style={{
                      height: '48px',
                      fontSize: '15px',
                    }}
                  >
                    Share
                  </Button>
                </Group>

                {/* Cart Button - Fixed Position */}
                <Box
                  style={{
                    position: 'fixed',
                    top: '12px',
                    right: '12px',
                    zIndex: 1000,
                    width: '48px',
                    height: '48px',
                  }}
                >
                  <ActionIcon
                    variant="filled"
                    size="xl"
                    radius="xl"
                    onClick={() => setCartOpened(true)}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      overflow: 'visible',
                    }}
                  >
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <IconShoppingCart />
                    </Box>
                    {getCartItemsCount() > 0 && (
                      <Box
                        style={{
                          position: 'absolute',
                          top: '-6px',
                          right: '-6px',
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          background: '#ef4444',
                          border: '3px solid white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          fontWeight: 700,
                          color: 'white',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                        }}
                      >
                        {getCartItemsCount() > 99 ? '99+' : getCartItemsCount()}
                      </Box>
                    )}
                  </ActionIcon>
                </Box>

                {/* Quick Trust Indicators */}
                <Group gap="xl" mt="xl">
                  <Group gap="xs">
                    <IconTruck size={20} color="#667eea" />
                    <Text size="sm" fw={600}>
                      {product.shipping}
                    </Text>
                  </Group>
                  <Group gap="xs">
                    <IconShield size={20} color="#667eea" />
                    <Text size="sm" fw={600}>
                      {product.returns}
                    </Text>
                  </Group>
                </Group>
              </Stack>
            </Grid.Col>
          </Grid>
        </Container>
      </Box>

      {/* Sticky Add to Cart Bar */}
      {isStickyVisible && (
        <Box
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'white',
            borderTop: '1px solid #e2e8f0',
            padding: '16px',
            zIndex: 1000,
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Container size="xl">
            <Group justify="space-between" align="center">
              <Group gap="lg">
                <Image
                  src={product.images[0]}
                  alt={product.name}
                  width={60}
                  height={60}
                  radius="md"
                  fit="cover"
                />
                <div>
                  <Text fw={700} size="lg">
                    {product.name}
                  </Text>
                  <Group gap="xs">
                    <Text size="xl" fw={800} c="blue">
                      ${product.price.toFixed(2)}
                    </Text>
                    <Text size="md" td="line-through" c="dimmed">
                      ${product.originalPrice.toFixed(2)}
                    </Text>
                  </Group>
                </div>
              </Group>
              <Group gap="md">
                <NumberInput
                  value={quantity}
                  onChange={(value) => setQuantity(Number(value) || 1)}
                  min={1}
                  max={10}
                  style={{ width: 100 }}
                  size="md"
                />
                <Button
                  size="lg"
                  leftSection={<IconShoppingCart size={20} />}
                  onClick={addToCart}
                  style={{
                    minWidth: '200px',
                    height: '48px',
                    fontSize: '16px',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  Add to Cart {getCartItemsCount() > 0 && `(${getCartItemsCount()})`}
                </Button>
              </Group>
            </Group>
          </Container>
        </Box>
      )}

      {/* Product Details Section */}
      <Container size="xl" py={{ base: 40, md: 80, lg: 100 }}>
        <Grid gutter="xl">
          {/* Left - Options */}
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="xl">
              {/* Color Selection */}
              <Paper
                p="xl"
                radius="lg"
                withBorder
                style={{
                  background: 'white',
                  borderColor: '#e2e8f0',
                }}
              >
                <Text fw={700} size="lg" mb="lg">
                  Choose Your Style
                </Text>
                <Group gap="sm">
                  {product.colors.map((color) => (
                    <Button
                      key={color}
                      variant={selectedColor === color ? 'filled' : 'outline'}
                      onClick={() => setSelectedColor(color)}
                      size="lg"
                      style={{
                        textTransform: 'capitalize',
                        flex: 1,
                        minWidth: '120px',
                        borderWidth: '2px',
                        fontWeight: selectedColor === color ? 700 : 500,
                        borderColor: selectedColor === color ? '#667eea' : '#e2e8f0',
                      }}
                    >
                      {color}
                    </Button>
                  ))}
                </Group>
              </Paper>

              {/* Quantity */}
              <Paper
                p="xl"
                radius="lg"
                withBorder
                style={{
                  background: 'white',
                  borderColor: '#e2e8f0',
                }}
              >
                <Text fw={700} size="lg" mb="lg">
                  Quantity
                </Text>
                <Group>
                  <NumberInput
                    value={quantity}
                    onChange={(value) => setQuantity(Number(value) || 1)}
                    min={1}
                    max={10}
                    style={{ width: 140 }}
                    size="lg"
                  />
                  <Text size="md" fw={600} c="dimmed">
                    {product.stock} available
                  </Text>
                </Group>
              </Paper>

              {/* Trust Badges */}
              <SimpleGrid cols={1} spacing="md">
                <Paper
                  p="xl"
                  radius="lg"
                  withBorder
                  style={{
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                    borderColor: '#e2e8f0',
                  }}
                >
                  <Stack gap="md">
                    <Group>
                      <IconTruck size={28} color="#667eea" />
                      <div>
                        <Text fw={700} size="sm">
                          Fast & Free Shipping
                        </Text>
                        <Text size="sm" c="dimmed">
                          {product.shipping}
                        </Text>
                      </div>
                    </Group>
                  </Stack>
                </Paper>
                <Paper
                  p="xl"
                  radius="lg"
                  withBorder
                  style={{
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                    borderColor: '#e2e8f0',
                  }}
                >
                  <Stack gap="md">
                    <Group>
                      <IconShield size={28} color="#667eea" />
                      <div>
                        <Text fw={700} size="sm">
                          Satisfaction Guaranteed
                        </Text>
                        <Text size="sm" c="dimmed">
                          {product.returns}
                        </Text>
                      </div>
                    </Group>
                  </Stack>
                </Paper>
              </SimpleGrid>
            </Stack>
          </Grid.Col>

          {/* Right - Features Grid */}
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Text fw={700} size="xl" mb="xl">
              Key Features
            </Text>
            <SimpleGrid cols={3} spacing="lg">
              {[
                { icon: IconFlame, title: 'Fireplace Effect', desc: 'Realistic flame simulation' },
                { icon: IconDroplet, title: 'Ultrasonic Mist', desc: 'Advanced technology' },
                { icon: IconBolt, title: 'USB Powered', desc: 'Easy connectivity' },
                { icon: IconWand, title: 'Remote Control', desc: 'Wireless operation' },
                { icon: IconSparkles, title: 'LED Lighting', desc: 'Colorful ambiance' },
                { icon: IconPackage, title: 'Complete Set', desc: 'Everything included' },
              ].map((feature, index) => (
                <Paper
                  key={index}
                  p="xl"
                  radius="lg"
                  withBorder
                  style={{
                    background: 'white',
                    borderColor: '#e2e8f0',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.currentTarget.style.transform = 'translateY(-6px)';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.15)';
                    e.currentTarget.style.borderColor = '#667eea';
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                >
                  <Stack gap="md" align="center">
                    <feature.icon size={36} color="#667eea" stroke={2} />
                    <Text fw={700} size="sm" ta="center">
                      {feature.title}
                    </Text>
                    <Text size="xs" c="dimmed" ta="center">
                      {feature.desc}
                    </Text>
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          </Grid.Col>
        </Grid>
      </Container>

      {/* Detailed Information Tabs */}
      <Box
        style={{
          background: '#f8f9fa',
          paddingTop: '40px',
          paddingBottom: '40px',
        }}
      >
        <Container size="xl">
          <Tabs defaultValue="description" variant="pills">
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '30px',
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              <Tabs.List style={{ gap: '8px', flexWrap: 'wrap' }}>
                <Tabs.Tab value="description" fw={700} size="md">
                  Overview
                </Tabs.Tab>
                <Tabs.Tab value="features" fw={700} size="md">
                  Features
                </Tabs.Tab>
                <Tabs.Tab value="specifications" fw={700} size="md">
                  Specifications
                </Tabs.Tab>
                <Tabs.Tab value="reviews" fw={700} size="md">
                  Reviews
                </Tabs.Tab>
              </Tabs.List>
            </Box>

            <Tabs.Panel value="description" pt="md">
              <Paper
                p="xl"
                radius="lg"
                style={{
                  background: 'white',
                  maxWidth: '1200px',
                  margin: '0 auto',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
                }}
              >
                <Stack gap="xl">
                  {/* Hero Section */}
                  <div>
                    <Text
                      size="xl"
                      fw={800}
                      mb="md"
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontSize: '2rem',
                      }}
                    >
                      Transform Your Living Space
                    </Text>
                    <Text
                      size="lg"
                      c="dimmed"
                      lh={1.8}
                      style={{ color: '#64748b', maxWidth: '800px' }}
                    >
                      Experience the perfect blend of aromatherapy and ambient lighting with our
                      realistic fireplace effect diffuser. Create a cozy atmosphere without fire
                      hazards.
                    </Text>
                  </div>

                  {/* Key Benefits Grid */}
                  <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
                    {[
                      {
                        icon: IconFlame,
                        title: 'Realistic Fireplace',
                        description:
                          'Beautiful simulated flame effect creates cozy ambiance without any fire hazards',
                        color: '#ef4444',
                      },
                      {
                        icon: IconDroplet,
                        title: 'Ultra-Fine Mist',
                        description:
                          'Ultrasonic technology creates smooth, even mist for perfect humidification',
                        color: '#3b82f6',
                      },
                      {
                        icon: IconSparkles,
                        title: 'Colorful Lighting',
                        description:
                          'Multi-color LED options for mood setting and relaxing atmosphere',
                        color: '#8b5cf6',
                      },
                      {
                        icon: IconWand,
                        title: 'Remote Control',
                        description:
                          'Convenient wireless control to adjust settings from anywhere in the room',
                        color: '#10b981',
                      },
                      {
                        icon: IconBolt,
                        title: 'USB Powered',
                        description:
                          'Easy USB connectivity perfect for use in any living space or office',
                        color: '#f59e0b',
                      },
                      {
                        icon: IconShield,
                        title: 'Safe & Certified',
                        description:
                          'CE & RoHS certified with water shortage protection for peace of mind',
                        color: '#6366f1',
                      },
                    ].map((benefit, index) => (
                      <Paper
                        key={index}
                        p="lg"
                        radius="lg"
                        withBorder
                        style={{
                          borderColor: '#e2e8f0',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                          e.currentTarget.style.borderColor = benefit.color;
                        }}
                        onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.borderColor = '#e2e8f0';
                        }}
                      >
                        <Stack gap="sm">
                          <benefit.icon size={36} color={benefit.color} stroke={2} />
                          <Text fw={700} size="lg">
                            {benefit.title}
                          </Text>
                          <Text size="sm" c="dimmed" lh={1.6}>
                            {benefit.description}
                          </Text>
                        </Stack>
                      </Paper>
                    ))}
                  </SimpleGrid>

                  <Divider />

                  {/* Perfect For Section */}
                  <div>
                    <Text fw={800} size="xl" mb="lg">
                      Perfect For
                    </Text>
                    <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
                      {[
                        { icon: '🏠', label: 'Living Rooms' },
                        { icon: '🛏️', label: 'Bedrooms' },
                        { icon: '💼', label: 'Offices' },
                        { icon: '🎁', label: 'Gifts' },
                        { icon: '🧘', label: 'Meditation' },
                        { icon: '📖', label: 'Reading' },
                        { icon: '💆', label: 'Relaxation' },
                        { icon: '🎉', label: 'Events' },
                      ].map((item, index) => (
                        <Paper
                          key={index}
                          p="md"
                          radius="md"
                          withBorder
                          style={{
                            textAlign: 'center',
                            borderColor: '#e2e8f0',
                          }}
                        >
                          <Text size="2rem" mb="xs">
                            {item.icon}
                          </Text>
                          <Text size="sm" fw={600}>
                            {item.label}
                          </Text>
                        </Paper>
                      ))}
                    </SimpleGrid>
                  </div>

                  <Divider />

                  {/* Key Highlights */}
                  <div>
                    <Text fw={800} size="xl" mb="lg">
                      Why Choose This Product?
                    </Text>
                    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                      {[
                        {
                          title: '150ml Large Capacity',
                          desc: 'Generous water capacity provides continuous operation for hours',
                        },
                        {
                          title: '20-25 ml/h Output',
                          desc: 'Optimal mist production for effective room humidification',
                        },
                        {
                          title: 'Timer Function',
                          desc: 'Intelligent timer with water shortage protection for safety',
                        },
                        {
                          title: 'BPA-Free Materials',
                          desc: 'Made from eco-friendly ABS plastic and PP materials',
                        },
                      ].map((highlight, index) => (
                        <Group key={index} gap="md" align="flex-start">
                          <ThemeIcon color="blue" size={32} radius="xl" variant="light">
                            <IconCheck size={18} />
                          </ThemeIcon>
                          <div style={{ flex: 1 }}>
                            <Text fw={700} size="md" mb={4}>
                              {highlight.title}
                            </Text>
                            <Text size="sm" c="dimmed" lh={1.6}>
                              {highlight.desc}
                            </Text>
                          </div>
                        </Group>
                      ))}
                    </SimpleGrid>
                  </div>
                </Stack>
              </Paper>
            </Tabs.Panel>

            <Tabs.Panel value="features" pt="md">
              <Paper
                p="xl"
                radius="lg"
                style={{
                  background: 'white',
                  maxWidth: '1000px',
                  margin: '0 auto',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
                }}
              >
                <List
                  spacing="lg"
                  size="lg"
                  icon={
                    <ThemeIcon color="blue" size={28} radius="xl" variant="light">
                      <IconCheck size={18} />
                    </ThemeIcon>
                  }
                >
                  {product.features.map((feature, index) => (
                    <List.Item key={index}>
                      <Text size="md" style={{ color: '#334155', lineHeight: 1.7 }}>
                        {feature}
                      </Text>
                    </List.Item>
                  ))}
                </List>
              </Paper>
            </Tabs.Panel>

            <Tabs.Panel value="specifications" pt="md">
              <Paper
                p="xl"
                radius="lg"
                style={{
                  background: 'white',
                  maxWidth: '1000px',
                  margin: '0 auto',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
                }}
              >
                <Stack gap="xl">
                  <div>
                    <Text fw={800} size="xl" mb="xl">
                      Product Specifications
                    </Text>
                    <SimpleGrid cols={2} spacing="xl">
                      {Object.entries(product.specifications).map(([key, value]) => (
                        <div key={key}>
                          <Text
                            size="xs"
                            fw={700}
                            c="dimmed"
                            style={{
                              textTransform: 'uppercase',
                              letterSpacing: '1.5px',
                              marginBottom: '8px',
                            }}
                          >
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </Text>
                          <Text size="lg" fw={600} style={{ color: '#1e293b' }}>
                            {value}
                          </Text>
                        </div>
                      ))}
                    </SimpleGrid>
                  </div>

                  <Divider />

                  <div>
                    <Text fw={800} size="xl" mb="xl">
                      Package Contents
                    </Text>
                    <List
                      spacing="md"
                      size="lg"
                      icon={
                        <ThemeIcon color="green" size={24} radius="xl" variant="light">
                          <IconCheck size={14} />
                        </ThemeIcon>
                      }
                    >
                      {product.packageContents.map((item, index) => (
                        <List.Item key={index}>
                          <Text size="md" fw={500}>
                            {item}
                          </Text>
                        </List.Item>
                      ))}
                    </List>
                  </div>
                </Stack>
              </Paper>
            </Tabs.Panel>

            <Tabs.Panel value="reviews" pt="md">
              <Paper
                p="xl"
                radius="lg"
                style={{
                  background: 'white',
                  maxWidth: '1000px',
                  margin: '0 auto',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
                }}
              >
                <Stack gap="xl">
                  {[
                    {
                      name: 'John D.',
                      rating: 5,
                      date: '2 weeks ago',
                      comment:
                        'Amazing product! The flame effect is so realistic and creates such a cozy atmosphere. The aromatherapy works perfectly and the remote control is very convenient. Highly recommend!',
                    },
                    {
                      name: 'Sarah M.',
                      rating: 5,
                      date: '1 month ago',
                      comment:
                        'Perfect for my living room! The humidification works great and the essential oils make the whole house smell wonderful. The LED colors are beautiful too.',
                    },
                    {
                      name: 'Michael R.',
                      rating: 5,
                      date: '3 weeks ago',
                      comment:
                        'Great gift idea! Bought this for my mom and she absolutely loves it. The timer function is very useful and the water shortage protection gives peace of mind.',
                    },
                    {
                      name: 'Emma L.',
                      rating: 4,
                      date: '1 week ago',
                      comment:
                        'Very satisfied with this purchase. The USB power is convenient and the mist output is perfect for my bedroom. Creates a really relaxing environment!',
                    },
                  ].map((review, index) => (
                    <div key={index}>
                      <Group justify="space-between" mb="md" align="flex-start">
                        <div>
                          <Group gap="md" mb="xs">
                            <Text fw={700} size="lg">
                              {review.name}
                            </Text>
                            <Rating value={review.rating} readOnly size="sm" />
                          </Group>
                          <Text size="sm" c="dimmed">
                            {review.date}
                          </Text>
                        </div>
                      </Group>
                      <Text size="md" c="dimmed" style={{ lineHeight: 1.8, color: '#64748b' }}>
                        {review.comment}
                      </Text>
                      {index < 3 && <Divider mt="xl" />}
                    </div>
                  ))}
                </Stack>
              </Paper>
            </Tabs.Panel>
          </Tabs>
        </Container>
      </Box>

      {/* Cart Modal */}
      <Modal
        opened={cartOpened}
        onClose={() => setCartOpened(false)}
        title={
          <Group>
            <IconShoppingCart size={24} color="#667eea" />
            <Text fw={700} size="xl">
              Shopping Cart
            </Text>
            {getCartItemsCount() > 0 && (
              <Badge size="lg" variant="light" color="blue">
                {getCartItemsCount()} {getCartItemsCount() === 1 ? 'item' : 'items'}
              </Badge>
            )}
          </Group>
        }
        size="xl"
        radius="lg"
      >
        {cart.length === 0 ? (
          <Stack align="center" gap="md" py="xl">
            <IconShoppingCart size={64} color="#cbd5e1" />
            <Text size="lg" fw={600} c="dimmed">
              Your cart is empty
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Add items to your cart to continue shopping
            </Text>
            <Button onClick={() => setCartOpened(false)} variant="light">
              Continue Shopping
            </Button>
          </Stack>
        ) : (
          <Stack gap="xl">
            <Stack gap="md">
              {cart.map((item) => (
                <Paper
                  key={item.id}
                  p="md"
                  radius="md"
                  withBorder
                  style={{
                    borderColor: '#e2e8f0',
                  }}
                >
                  <Group gap="md" align="flex-start">
                    <Image
                      src={item.image}
                      alt={item.name}
                      width={80}
                      height={80}
                      radius="md"
                      fit="cover"
                    />
                    <Stack gap="xs" style={{ flex: 1 }}>
                      <Text fw={600} size="md">
                        {item.name}
                      </Text>
                      {item.color && (
                        <Text size="sm" c="dimmed">
                          Color: {item.color}
                        </Text>
                      )}
                      <Group justify="space-between" align="center">
                        <Group gap="xs">
                          <Text fw={700} size="lg" c="blue">
                            ${item.price.toFixed(2)}
                          </Text>
                          <Text size="sm" c="dimmed" td="line-through">
                            ${(item.price * 1.5).toFixed(2)}
                          </Text>
                        </Group>
                        <ActionIcon
                          variant="light"
                          color="red"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Group>
                      <Group gap="sm" align="center">
                        <Text size="sm" fw={500}>
                          Quantity:
                        </Text>
                        <NumberInput
                          value={item.quantity}
                          onChange={(value) => updateCartQuantity(item.id, Number(value) || 1)}
                          min={1}
                          max={10}
                          style={{ width: 100 }}
                          size="sm"
                        />
                        <Text size="sm" fw={600} style={{ marginLeft: 'auto' }}>
                          Subtotal: ${(item.price * item.quantity).toFixed(2)}
                        </Text>
                      </Group>
                    </Stack>
                  </Group>
                </Paper>
              ))}
            </Stack>

            <Divider />

            <Stack gap="md">
              <Group justify="space-between">
                <Text size="lg" fw={600}>
                  Subtotal:
                </Text>
                <Text size="lg" fw={700}>
                  ${getCartTotal().toFixed(2)}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Shipping:
                </Text>
                <Text size="sm" fw={600} c={getCartTotal() >= 40 ? 'green' : 'dimmed'}>
                  {getCartTotal() >= 40 ? 'FREE' : '$5.99'}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="xl" fw={700}>
                  Total:
                </Text>
                <Text size="xl" fw={900} c="blue">
                  ${(getCartTotal() + (getCartTotal() >= 40 ? 0 : 5.99)).toFixed(2)}
                </Text>
              </Group>
            </Stack>

            <Group gap="md" mt="xl">
              <Button variant="outline" fullWidth onClick={() => setCartOpened(false)}>
                Continue Shopping
              </Button>
              <Button
                fullWidth
                size="lg"
                onClick={handleCheckout}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                Proceed to Checkout
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Checkout Modal */}
      <Modal
        opened={checkoutOpened}
        onClose={() => !orderPlaced && setCheckoutOpened(false)}
        title={
          <Group>
            <IconCreditCard size={24} color="#667eea" />
            <Text fw={700} size="xl">
              Checkout
            </Text>
          </Group>
        }
        size="lg"
        radius="lg"
        closeOnClickOutside={!orderPlaced}
        closeOnEscape={!orderPlaced}
      >
        {orderPlaced ? (
          <Stack align="center" gap="xl" py="xl">
            <Box
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconCheck size={48} color="white" />
            </Box>
            <Stack align="center" gap="xs">
              <Text size="xl" fw={700}>
                Order Placed Successfully!
              </Text>
              <Text size="md" c="dimmed" ta="center">
                Thank you for your purchase. You will receive a confirmation email shortly.
              </Text>
            </Stack>
          </Stack>
        ) : (
          <Stack gap="xl">
            {/* Shipping Information */}
            <div>
              <Text fw={700} size="lg" mb="md">
                Shipping Information
              </Text>
              <Stack gap="md">
                <TextInput
                  label="Full Name"
                  placeholder="John Doe"
                  leftSection={<IconMapPin size={18} />}
                  value={checkoutForm.fullName}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, fullName: e.target.value })}
                  required
                />
                <Group grow>
                  <TextInput
                    label="Email"
                    placeholder="john@example.com"
                    type="email"
                    leftSection={<IconMail size={18} />}
                    value={checkoutForm.email}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, email: e.target.value })}
                    required
                  />
                  <TextInput
                    label="Phone"
                    placeholder="+1 234 567 8900"
                    leftSection={<IconPhone size={18} />}
                    value={checkoutForm.phone}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, phone: e.target.value })}
                    required
                  />
                </Group>
                <TextInput
                  label="Address"
                  placeholder="123 Main Street"
                  leftSection={<IconMapPin size={18} />}
                  value={checkoutForm.address}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, address: e.target.value })}
                  required
                />
                <Group grow>
                  <TextInput
                    label="City"
                    placeholder="New York"
                    value={checkoutForm.city}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, city: e.target.value })}
                    required
                  />
                  <TextInput
                    label="Postal Code"
                    placeholder="10001"
                    value={checkoutForm.postalCode}
                    onChange={(e) =>
                      setCheckoutForm({ ...checkoutForm, postalCode: e.target.value })
                    }
                    required
                  />
                  <Select
                    label="Country"
                    placeholder="Select country"
                    data={['United States', 'Canada', 'United Kingdom', 'Germany', 'France']}
                    value={checkoutForm.country}
                    onChange={(value) => setCheckoutForm({ ...checkoutForm, country: value || '' })}
                    required
                  />
                </Group>
              </Stack>
            </div>

            <Divider />

            {/* Payment Method */}
            <div>
              <Text fw={700} size="lg" mb="md">
                Payment Method
              </Text>
              <Select
                placeholder="Select payment method"
                data={['Credit Card', 'PayPal', 'Bank Transfer', 'Cash on Delivery']}
                value={checkoutForm.paymentMethod}
                onChange={(value) =>
                  setCheckoutForm({ ...checkoutForm, paymentMethod: value || '' })
                }
                required
              />
            </div>

            <Divider />

            {/* Order Summary */}
            <div>
              <Text fw={700} size="lg" mb="md">
                Order Summary
              </Text>
              <Paper p="md" radius="md" withBorder>
                <Stack gap="sm">
                  {cart.map((item) => (
                    <Group justify="space-between" key={item.id}>
                      <Text size="sm">
                        {item.name} {item.color && `(${item.color})`} x{item.quantity}
                      </Text>
                      <Text size="sm" fw={600}>
                        ${(item.price * item.quantity).toFixed(2)}
                      </Text>
                    </Group>
                  ))}
                  <Divider />
                  <Group justify="space-between">
                    <Text fw={600}>Subtotal:</Text>
                    <Text fw={600}>${getCartTotal().toFixed(2)}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Shipping:
                    </Text>
                    <Text size="sm" fw={600} c={getCartTotal() >= 40 ? 'green' : 'dimmed'}>
                      {getCartTotal() >= 40 ? 'FREE' : '$5.99'}
                    </Text>
                  </Group>
                  <Divider />
                  <Group justify="space-between">
                    <Text size="lg" fw={700}>
                      Total:
                    </Text>
                    <Text size="lg" fw={900} c="blue">
                      ${(getCartTotal() + (getCartTotal() >= 40 ? 0 : 5.99)).toFixed(2)}
                    </Text>
                  </Group>
                </Stack>
              </Paper>
            </div>

            <Group gap="md" mt="xl">
              <Button variant="outline" fullWidth onClick={() => setCheckoutOpened(false)}>
                Back to Cart
              </Button>
              <Button
                fullWidth
                size="lg"
                onClick={handlePlaceOrder}
                disabled={
                  !checkoutForm.fullName ||
                  !checkoutForm.email ||
                  !checkoutForm.phone ||
                  !checkoutForm.address ||
                  !checkoutForm.city ||
                  !checkoutForm.postalCode ||
                  !checkoutForm.country ||
                  !checkoutForm.paymentMethod
                }
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                Place Order
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Box>
  );
}
